import { memo } from 'react';
import styled from 'styled-components';

import type { CommonBaseModalProps } from '@front/common/src/types/components';
import colors from '@front/common/src/utils/colors';
import { toRem } from '@front/common/src/utils/styles';

import { translate } from '@front/i18n';

import { DESKTOP_GRID_WIDTH } from '../../constants/layout';
import { minWidth } from '../../styles/mixins';
import Title from '../base-text/variants/title';
import IconButton from '../button/icon-button';
import AccessibleModal from './accessible-modal';
import { slideDown, slideUp } from './common';
import ModalContainerWithBackdrop, { hide, MODAL_ANIMATION_DURATION, show } from './modal-container-with-backdrop';

const t = translate('components.modal');

type StyledModalProps = { $open: boolean };

const StyledModal = styled(AccessibleModal)<StyledModalProps>`
  position: absolute;
  width: 100%;
  bottom: 0;
  animation: ${({ $open }) => ($open ? slideUp : slideDown)} ${MODAL_ANIMATION_DURATION}ms ease forwards;

  ${minWidth.md`
    max-width: ${toRem(DESKTOP_GRID_WIDTH)};
    max-height: 90%;
    width: 70%;
    bottom: auto;

    animation: ${({ $open }: StyledModalProps) => ($open ? show : hide)} ${MODAL_ANIMATION_DURATION}ms ease forwards;
  `}

  ${minWidth.lg`
    width: 50%;
  `}
`;

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  top: ${toRem(15)};
  right: ${toRem(15)};
  z-index: 102;
`;

/*
 * StyledModal (the parent element of this container) has `position: relative` style.
 * It must also be defined here because children of `ModalBody` with `position: absolute`
 * might overflow the `border-radius`, and `overflow: hidden` set here will only work if they
 * are positioned relatively to this container.
 */
const ModalBody = styled.div`
  position: relative;
  border-radius: ${toRem(30)} ${toRem(30)} 0 0;
  overflow: auto;
  max-height: 85vh;
  height: 100%;
  background-color: ${colors.white100};

  ${minWidth.md`
    border-radius: ${toRem(30)};
  `}
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${toRem(30)} ${toRem(50)} ${toRem(20)};
  border-radius: ${toRem(30)} ${toRem(30)} 0 0;
  background-color: ${colors.white100};
  z-index: 101;
  position: sticky;
  top: 0;
`;

const BaseModal = ({ isOpen, title, children, onClose }: CommonBaseModalProps) => (
  <ModalContainerWithBackdrop isOpen={isOpen} onClose={onClose}>
    <StyledModal aria-labelledby='modalTitle' $open={isOpen}>
      {onClose && (
        <StyledCloseButton
          type='secondary'
          iconName='no'
          size={30}
          iconSize={12}
          onPress={onClose}
          label={t('.close')}
        />
      )}
      <ModalBody>
        {title && (
          <ModalTitle>
            <Title.MD centered={true} id='modalTitle'>
              {title}
            </Title.MD>
          </ModalTitle>
        )}
        {children}
      </ModalBody>
    </StyledModal>
  </ModalContainerWithBackdrop>
);

export default memo(BaseModal);
